import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authClient } from 'src/services/auth/client';
import { clearAccessToken } from 'src/services/auth/tokenStore';
import { QUERIES } from 'src/utils/react-query';
import { deriveSlug, nextAvailableSlug } from 'src/utils/slug';

export function useMemberships() {
  return useQuery({
    queryKey: [QUERIES.MEMBERSHIPS],
    queryFn: async () => {
      const { data, error } = await authClient.organization.list();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; slug: string; logo?: string }) => {
      const { data, error } = await authClient.organization.create(input);
      if (error) throw new Error(error.message);
      return data!;
    },
    onSuccess: async (organization) => {
      await authClient.organization.setActive({ organizationId: organization.id });
      // The cached access token was minted before setActive and carries no org claims;
      // drop it so the status refetch below gets a fresh JWT (15-min cache otherwise).
      clearAccessToken();
      // Authoritative read: refresh the session_data cookie view before anything fetches
      // the active member (a first get-active-member can otherwise race the cookie write).
      await authClient.getSession({ query: { disableCookieCache: true } });
      await queryClient.invalidateQueries({ queryKey: [QUERIES.MEMBERSHIPS] });
      await queryClient.invalidateQueries({ queryKey: [QUERIES.ONBOARDING_STATUS] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { organizationId: string; name?: string; slug?: string; logo?: string }) => {
      const { organizationId, ...data } = input;
      const { error } = await authClient.organization.update({ organizationId, data });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.MEMBERSHIPS] }),
  });
}

/**
 * Slug collisions never block the wizard: the suggestion auto-suffixes until it is free,
 * and the field stays editable so a user can pick their own.
 */
export async function suggestSlug(name: string): Promise<string> {
  const base = deriveSlug(name);
  const taken: string[] = [];
  for (const candidate of [base, `${base}-2`, `${base}-3`, `${base}-4`, `${base}-5`]) {
    const { data } = await authClient.organization.checkSlug({ slug: candidate });
    if (data?.status) {
      return candidate;
    }
    taken.push(candidate);
  }
  return nextAvailableSlug(base, taken);
}

export function useSlugAvailability(slug: string) {
  return useQuery({
    queryKey: ['slugAvailability', slug],
    enabled: slug.length > 2,
    queryFn: async () => {
      const { data } = await authClient.organization.checkSlug({ slug });
      return Boolean(data?.status);
    },
  });
}
